import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, StatusBadge, Loading, Tag } from "@/components/common";
import { FilmSlate, Plus, CaretRight, GraduationCap, CurrencyDollar, Clock, Translate, X,
  PencilSimple, Copy, Archive, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = ["IDEA", "SCRIPT", "STORYBOARD", "SCENES", "VOICES", "ANIMATION", "MUSIC/SFX", "EDIT", "LOCALIZE", "REVIEW", "APPROVED", "SCHEDULED", "PUBLISHED"];

export default function EpisodeStudio() {
  const { active } = useStudio();
  const [eps, setEps] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/episodes").then(({ data }) => setEps(data));
  useEffect(() => { load(); }, [active?.id]);

  const doRename = async (ep) => {
    const n = window.prompt("Rename episode", ep.title);
    if (n && n !== ep.title) { await api.patch(`/content/episodes/${ep.id}`, { data: { title: n } }); toast.success("Renamed"); load(); }
  };
  const doDuplicate = async (ep) => { await api.post(`/content/episodes/${ep.id}/duplicate`); toast.success("Duplicated"); load(); };
  const doArchive = async (ep) => { await api.post(`/content/episodes/${ep.id}/archive`); toast.success("Archived"); load(); };
  const doDelete = async (ep) => { if (window.confirm(`Move "${ep.title}" to Trash?`)) { await api.delete(`/content/episodes/${ep.id}`); toast.success("Moved to Trash"); load(); } };

  if (!eps) return <Loading />;

  return (
    <div>
      <PageHeader icon={FilmSlate} title="Episode Studio"
        subtitle="Every episode moves through a visual production pipeline from idea to published — with per-language master versions."
        actions={<button data-testid="new-episode" onClick={() => toast("Describe your episode in the studio assistant to start the pipeline.")}
          className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black"><Plus size={16} weight="bold" /> New Episode</button>} />

      <div className="space-y-5">
        {eps.map((ep) => (
          <Panel key={ep.id} data-testid={`ep-${ep.id}`} className="p-0 overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="relative lg:w-64 h-44 lg:h-auto shrink-0">
                <img src={ep.image} alt={ep.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 scrim lg:bg-none" />
                <span className="absolute top-3 left-3 font-mono text-xs bg-black/60 rounded px-2 py-1 text-white">EP {ep.number}</span>
              </div>
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-xl font-bold text-white">{ep.title}</h3>
                      <StatusBadge status={ep.stage} />
                    </div>
                    <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{ep.synopsis}</p>
                  </div>
                  <button data-testid={`ep-open-${ep.id}`} onClick={() => setOpen(ep)} className="shrink-0 text-[#00E5FF] hover:underline text-sm flex items-center gap-1">Open <CaretRight size={14} /></button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid={`ep-rename-${ep.id}`} onClick={() => doRename(ep)} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-white hover:border-white/30"><PencilSimple size={13} /> Rename</button>
                  <button data-testid={`ep-dup-${ep.id}`} onClick={() => doDuplicate(ep)} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-white hover:border-white/30"><Copy size={13} /> Duplicate</button>
                  <button data-testid={`ep-archive-${ep.id}`} onClick={() => doArchive(ep)} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-[#00E5FF] hover:border-white/30"><Archive size={13} /> Archive</button>
                  <button data-testid={`ep-delete-${ep.id}`} onClick={() => doDelete(ep)} className="inline-flex items-center gap-1 rounded-full border border-[#FF3366]/40 px-3 py-1.5 text-xs text-[#FF3366] hover:bg-[#FF3366]/10"><Trash size={13} /> Delete</button>
                </div>

                <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Clock size={14} weight="duotone" /> {ep.duration}</span>
                  <span className="flex items-center gap-1"><GraduationCap size={14} weight="duotone" className="text-[#00FF9D]" /> {ep.learning}</span>
                  <span className="flex items-center gap-1"><CurrencyDollar size={14} weight="duotone" className="text-[#FFB800]" /> ${ep.cost.toFixed(2)}</span>
                  <span>{ep.scenes} scenes · Age {ep.age}</span>
                </div>

                <Pipeline stage={ep.stage} progress={ep.progress} />
              </div>
            </div>
          </Panel>
        ))}
      </div>

      {open && <EpisodeDrawer ep={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function Pipeline({ stage, progress }) {
  const currentIdx = STAGES.indexOf(stage);
  return (
    <div className="mt-4">
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {STAGES.map((s, i) => (
          <div key={s} className={`flex items-center gap-1 shrink-0 ${i <= currentIdx ? "opacity-100" : "opacity-35"}`}>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${i < currentIdx ? "bg-[#00FF9D]/15 text-[#00FF9D]" : i === currentIdx ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/5 text-zinc-500"}`}>{s}</span>
            {i < STAGES.length - 1 && <CaretRight size={10} className="text-zinc-700" />}
          </div>
        ))}
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
        <div className="h-full bg-magic rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function EpisodeDrawer({ ep, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md glass h-full overflow-y-auto p-6 animate-fadeUp">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-black">{ep.title}</h3>
          <button onClick={onClose} data-testid="ep-drawer-close" className="text-zinc-400 hover:text-white"><X size={22} /></button>
        </div>
        <img src={ep.image} alt="" className="rounded-xl w-full h-40 object-cover mb-4" />
        <p className="text-sm text-zinc-300 mb-4">{ep.synopsis}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {ep.characters?.map((c) => <Tag key={c}>{c}</Tag>)}
          <Tag>{ep.world}</Tag>
        </div>

        <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Translate size={16} weight="duotone" className="text-[#00E5FF]" /> Master + Language Versions</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/8 px-3 py-2">
            <span className="text-sm font-semibold text-white">MASTER (English)</span>
            <StatusBadge status={ep.stage === "PUBLISHED" ? "PUBLISHED" : "APPROVED"} />
          </div>
          {ep.lang_versions?.map((lv) => (
            <div key={lv.language} data-testid={`ep-lang-${lv.language}`} className="flex items-center justify-between rounded-lg border border-white/8 bg-black/20 px-3 py-2">
              <span className="text-sm text-white">{lv.language}</span>
              <div className="flex items-center gap-2">
                <StatusBadge status={lv.status} />
                {(lv.status === "APPROVED" || lv.status === "READY FOR REVIEW") && (
                  <button className="text-xs text-[#00E5FF] hover:underline">Watch</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
